import React, { useMemo } from 'react';
import { OrderCreateEntry } from 'public-contracts';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';

import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useTranslation } from '../hooks/useTranslation';
import { ordersService } from '../services/ordersService';
import { useNavigate } from 'react-router-dom';

// Import existing components that we'll reuse
import { ModernCustomerSelector } from './components/ModernCustomerSelector';
import { ModernItemsGrid } from './components/ModernItemsGrid';
import { OrderTotalField } from './components/OrderTotalField';
import { PointsRedemption } from './components/PointsRedemption';
import { ModernTableSelector } from './components/ModernTableSelector';
import { useTableStatusStore } from '../stores/useTableStatusStore';
import { PlatformLogo } from './components/PlatformLogos';

// Form data interface extending OrderCreateEntry
interface OrderFormData
  extends Omit<
    OrderCreateEntry,
    | 'id'
    | 'paid'
    | 'pointsEarned'
    | 'promotionId'
    | 'taxes'
    | 'taxRate'
    | 'total'
    | 'totalExTaxes'
    | 'deliveryFees'
    | 'tenantId'
  > {
  promotionType: 'none' | 'percentage' | 'fixed';
  promotionValue: number;
  customerPoints: number | null;
  pointsRedemption: number;
  orderType: 'dine_in' | 'takeaway' | 'delivery';
  sourcePlatform?: 'grab' | 'shopee' | 'xanhsm' | 'internal';
  tableNumber?: number;
  notes?: string;
  items: Array<{
    productId: string;
    quantity: number;
    price: number;
    note?: string;
  }>;
  date: Date;
  customerId: string;
  status: 'ordered' | 'ready' | 'delivered' | 'cancelled';
  hasCheckedIn: boolean;
  tempCustomerId?: string;
}

const generateReference = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `ORD${year}${month}${day}${hours}${minutes}${seconds}`;
};

const PromotionSection = () => {
  const { translate } = useTranslation();
  const { watch, setValue } = useFormContext<OrderFormData>();

  const promotionType = watch('promotionType');
  const items = watch('items') || [];
  const customerPoints = watch('customerPoints');

  const totalAmount = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {translate('pos.orders.fields.promotion')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              {translate('pos.orders.fields.promotionType')}
            </label>
            <select
              className="w-full p-2 border border-input rounded-md bg-background text-foreground"
              value={promotionType}
              onChange={(e) => {
                const newType = e.target.value as
                  | 'none'
                  | 'percentage'
                  | 'fixed';
                setValue('promotionType', newType);
                // Always reset promotion value when type changes
                // This ensures old values don't persist when switching between types
                setValue('promotionValue', 0);
              }}
              aria-label={translate(
                'pos.orders.fields.promotionType'
              )}
            >
              <option value="none">
                {translate('pos.orders.promotion.none')}
              </option>
              <option value="percentage">
                {translate('pos.orders.promotion.percentage')}
              </option>
              <option value="fixed">
                {translate('pos.orders.promotion.fixed')}
              </option>
            </select>
          </div>

          {promotionType !== 'none' && (
            <div>
              <label className="text-sm font-medium mb-2 block">
                {translate('pos.orders.fields.promotionValue')}
              </label>
              <input
                type="number"
                className="w-full p-2 border border-input rounded-md bg-background text-foreground"
                min={0}
                max={promotionType === 'percentage' ? 100 : totalAmount}
                value={watch('promotionValue') || 0}
                onChange={(e) =>
                  setValue('promotionValue', Number(e.target.value))
                }
              />
            </div>
          )}

          {promotionType === 'none' && (
            <PointsRedemption
              customerPoints={Number(customerPoints || 0)}
              totalAmount={totalAmount}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const OrderForm = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const reference = useMemo(() => generateReference(), []);
  const [submitError, setSubmitError] = React.useState<string>('');
  const { fetchTableStatuses } = useTableStatusStore();

  const form = useForm<OrderFormData>({
    defaultValues: {
      reference,
      promotionType: 'none',
      promotionValue: 0,
      customerPoints: null,
      pointsRedemption: 0,
      orderType: 'dine_in',
      sourcePlatform: undefined,
      tableNumber: undefined,
      items: [],
      date: new Date(),
      status: 'ordered',
      customerId: '',
      hasCheckedIn: false,
      tempCustomerId: undefined,
    },
  });

  const { watch } = form;
  const orderType = watch('orderType');
  const sourcePlatform = watch('sourcePlatform');
  const tableNumber = watch('tableNumber');
  const items = watch('items') || [];
  const tempCustomerId = watch('tempCustomerId');
  const hasCheckedIn = watch('hasCheckedIn');
  const orderDate = watch('date');

  const isDineIn = orderType === 'dine_in';
  const hasTable = Boolean(tableNumber);
  const hasItems = items.length > 0;
  const hasSelectedCustomer = Boolean(tempCustomerId);
  // Allow customer tagging without check-in for walk-in customers
  // Check-in is only required for membership benefits (points redemption, etc.)
  const isCustomerValid = true; // Customer selection is now optional

  const isValidForm =
    hasItems && (!isDineIn || (isDineIn && hasTable)) && isCustomerValid;

  let errorMessage = '';
  if (!hasItems) {
    errorMessage = translate('pos.orders.messages.selectProducts');
  } else if (isDineIn && !hasTable) {
    errorMessage = translate('pos.orders.messages.selectTableFirst');
  }
  // Removed check-in requirement - customer tagging is now optional

  return (
    <FormProvider {...form}>
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">
              {translate('pos.orders.create')}
            </h1>
            <p className="text-muted-foreground">
              {translate('pos.orders.fields.reference')}: {reference}
            </p>
          </div>
          <Badge variant="outline" className="text-sm self-start sm:self-auto">
            {translate('pos.orders.status.ordered')}
          </Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-3 space-y-6">
            {/* Order Type and Table Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {translate('pos.orders.fields.orderType')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    {
                      id: 'dine_in',
                      name: translate('pos.orders.orderTypes.dineIn'),
                    },
                    {
                      id: 'takeaway',
                      name: translate(
                        'pos.orders.orderTypes.takeaway'
                      ),
                    },
                    {
                      id: 'delivery',
                      name: translate(
                        'pos.orders.orderTypes.delivery'
                      ),
                    },
                  ].map((type) => (
                    <Button
                      key={type.id}
                      variant={orderType === type.id ? 'default' : 'outline'}
                      className="h-12"
                      onClick={() => form.setValue('orderType', type.id as any)}
                    >
                      {type.name}
                    </Button>
                  ))}
                </div>

                {isDineIn && <ModernTableSelector />}

                {/* Delivery Platform Selection - Only show for delivery orders */}
                {orderType === 'delivery' && (
                  <div className="mt-4">
                    <label className="text-sm font-medium mb-2 block">
                      {translate(
                        'pos.orders.fields.deliveryPlatform',
                        'Delivery Platform'
                      )}
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        {
                          id: 'internal',
                          name: translate(
                            'pos.orders.deliveryPlatforms.internal',
                            'Internal'
                          ),
                        },
                        {
                          id: 'grab',
                          name: translate(
                            'pos.orders.deliveryPlatforms.grab',
                            'Grab'
                          ),
                        },
                        {
                          id: 'shopee',
                          name: translate(
                            'pos.orders.deliveryPlatforms.shopee',
                            'Shopee'
                          ),
                        },
                        {
                          id: 'xanhsm',
                          name: translate(
                            'pos.orders.deliveryPlatforms.xanhsm',
                            'Xanh SM'
                          ),
                        },
                      ].map((platform) => (
                        <Button
                          key={platform.id}
                          type="button"
                          variant={
                            sourcePlatform === platform.id
                              ? 'default'
                              : 'outline'
                          }
                          className="h-10 text-sm flex items-center justify-center gap-2"
                          onClick={() =>
                            form.setValue('sourcePlatform', platform.id as any)
                          }
                        >
                          <PlatformLogo
                            platform={platform.id as any}
                            size={18}
                          />
                          {platform.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Customer Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {translate('pos.orders.fields.customer')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ModernCustomerSelector />
              </CardContent>
            </Card>

            {/* Items Selection */}
            <ModernItemsGrid disabled={false} />
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4 order-first lg:order-last">
            {/* Order Total - Sticky */}
            <div className="lg:sticky lg:top-4 z-10">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {translate('pos.orders.fields.total')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <OrderTotalField />
                </CardContent>
              </Card>
            </div>
            {/* Promotion Section */}
            <PromotionSection />

            {/* Date Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {translate('pos.orders.fields.date')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <input
                  type="datetime-local"
                  className="w-full p-2 border border-input rounded-md bg-background text-foreground disabled:bg-muted disabled:cursor-not-allowed"
                  value={
                    orderDate
                      ? new Date(orderDate).toISOString().slice(0, 16)
                      : new Date().toISOString().slice(0, 16)
                  }
                  readOnly
                  disabled
                />
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <Button
                    data-testid="order-submit-button"
                    className="w-full"
                    size="lg"
                    disabled={!isValidForm}
                    onClick={(e) => {
                      console.log(
                        '🔘 Save button clicked! isValidForm:',
                        isValidForm
                      );
                      form.handleSubmit(async (data) => {
                        try {
                          setSubmitError('');
                          console.log('📦 Creating order with data:', data);

                          // Transform form data to API format
                          // Use tempCustomerId if available (allows tagging walk-in customers)
                          // Only use customerId if customer has checked in (for membership benefits)
                          const orderData = {
                            reference: data.reference,
                            orderType: data.orderType,
                            tableNumber: data.tableNumber,
                            items: data.items,
                            date: data.date,
                            // Allow customer tagging even without check-in for walk-in customers
                            // This enables customer metrics tracking in campaign reports
                            customerId:
                              data.customerId ||
                              data.tempCustomerId ||
                              undefined,
                            status: data.status,
                            notes: data.notes || '',
                            promotionType: data.promotionType,
                            promotionValue: data.promotionValue,
                            // Only allow points redemption if customer has checked in
                            pointsRedemption: hasCheckedIn
                              ? data.pointsRedemption
                              : 0,
                            // Include source platform if specified (for delivery orders)
                            sourcePlatform: data.sourcePlatform || undefined,
                          };

                          console.log('🚀 Submitting order to API:', orderData);
                          const result = await ordersService.createOrder(
                            orderData
                          );
                          console.log('✅ Order created successfully:', result);

                          // Update table status if a table was booked
                          if (data.tableNumber && result?.id) {
                            const { occupyTable } =
                              useTableStatusStore.getState();
                            try {
                              await occupyTable(data.tableNumber, result.id);
                            } catch (error) {
                              console.error(
                                'Failed to update table status:',
                                error
                              );
                              // Don't fail the order creation if table status update fails
                            }
                            await fetchTableStatuses();
                          }

                          // Navigate to the created order or orders list
                          if (result?.id) {
                            console.log('📍 Navigating to order:', result.id);
                            navigate(`/orders/${result.id}`);
                          } else {
                            console.log('📍 Navigating to orders list');
                            navigate('/orders');
                          }
                        } catch (error) {
                          console.error('❌ Error creating order:', error);
                          const errorMsg =
                            error instanceof Error
                              ? error.message
                              : 'Failed to create order. Please try again.';
                          setSubmitError(errorMsg);
                          // Don't navigate - stay on form so user can retry
                        }
                      })(e);
                    }}
                  >
                    {translate('pos.common.action.save')}
                  </Button>

                  {errorMessage && (
                    <p className="text-sm text-destructive text-center">
                      {errorMessage}
                    </p>
                  )}

                  {submitError && (
                    <p className="text-sm text-destructive text-center font-semibold">
                      ⚠️ {submitError}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};

export const ModernOrderCreate = () => {
  return <OrderForm />;
};

export default ModernOrderCreate;
